// AI-CONTEXT: See docs/patterns.md#ui-svg-edge-overlay
// AI-CONTEXT: See docs/features/toc-drag-drop-links.md
import { useMemo, useRef, useState, useCallback, useLayoutEffect, useEffect } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { STEP_ORDER } from "../../helpers/tocHelpers/tocNavigatorChain";
import buildChainEdges from "../../helpers/tocHelpers/buildChainEdges";
import resolveLinkTarget from "../../helpers/tocHelpers/resolveLinkTarget";
import TocChainNode from "./TocChainNode.jsx";

// Module scope on purpose: an inline options object is a new identity every
// render, which misses useSensor's memo → useSensors' memo → and invalidates
// every DndContext on the board. 8px activation distance so a click on the grip
// that never moves still behaves like a click; the keyboard sensor makes
// reordering reachable without a pointer.
const POINTER_ACTIVATION = { activationConstraint: { distance: 8 } };
const KEYBOARD_COORDINATES = { coordinateGetter: sortableKeyboardCoordinates };

/**
 * "Overzicht" board for the TOC navigator: four columns (doelgroep → activiteit
 * → mechanisme → effect) with every live element and one SVG overlay drawing the
 * links between them. The board renders no header of its own — the page's
 * breadcrumb (.tocnav-bc) is the single header, and each column aligns under its
 * breadcrumb step (shared 5-track grid). Hovering a card highlights its connected
 * chain and dims the rest ("geen permanente spaghetti" — relations pop on
 * interaction). Clicking a card jumps to that element's detail.
 *
 * Cards carry an outgoing connect-dot (all steps except effect, which is
 * terminal). Dragging from a dot draws a live line and, when dropped on a valid
 * adjacent card (left→right), calls onLink to create the link. Validity comes
 * from resolveLinkTarget, so the highlight and the write stay in sync.
 *
 * Cards also carry a grip handle on their left edge that reorders them WITHIN
 * their own column (each column is its own DndContext, so a card can never be
 * dropped into another step's collection). The new order is persisted as
 * Position by the caller — the same field the board and the portfolio subscribe
 * modal both read — so the order the user arranges here is the order everywhere.
 *
 * @param {object} props
 * @param {{byStep: Record<string, Array<object>>, byId: Map<string, object>}} props.model - navigator model
 * @param {Record<string, {label: string, color: string}>} props.stepMeta - per-step display metadata
 * @param {(id: string, level: number) => void} props.onPick - open a node's detail
 * @param {(step: string) => void} [props.onAdd] - add a new item to a column (add bar button); absent → read-only board, no add bar
 * @param {(fromId: string, toId: string) => void} [props.onLink] - create a link from a drag-drop (drag source → drop target)
 * @param {(fromId: string, toId: string) => void} [props.onUnlink] - remove an existing link (edge remove control)
 * @param {Record<string, string>} [props.syncedLinks] - local element ID → parent portfolio CompagnyID (drives the linked badge)
 * @param {Record<string, string>} [props.portfolioNameById] - portfolio CompagnyID → display name (badge tooltip)
 * @param {(step: string, activeId: string, overId: string) => void} [props.onReorder] - move a card within its column (drag handle); absent → no handles, no reordering
 */
const TocChainOverview = ({ model, stepMeta, onPick, onAdd, onLink, onUnlink, syncedLinks, portfolioNameById, onReorder }) => {
  const [hoverId, setHoverId] = useState(/** @type {string | null} */ (null));
  // The edge whose remove control is showing (hovered edge). Cleared by leaving
  // the control or the board — not by leaving the edge — so the cursor can cross
  // the gap from line to button without the control vanishing under it.
  const [activeEdge, setActiveEdge] = useState(/** @type {{from: string, to: string} | null} */ (null));

  const edges = useMemo(() => buildChainEdges(model), [model]);

  // ── drag-to-link state ──
  // drag: the source node ID while a link is being drawn (null when idle).
  const [drag, setDrag] = useState(/** @type {string | null} */ (null));
  // dragOverId: the card the pointer is currently over during a drag (snaps the
  // live line and gives the exact target a stronger highlight).
  const [dragOverId, setDragOverId] = useState(/** @type {string | null} */ (null));
  // dragCursor: pointer position relative to the board, for the loose line end.
  const [dragCursor, setDragCursor] = useState(/** @type {{x: number, y: number} | null} */ (null));

  // Connected set for the hovered node: everything reachable downstream plus
  // everything upstream — the causal chain through that node.
  const connected = useMemo(() => {
    if (!hoverId) return null;
    const set = new Set([hoverId]);
    let frontier = [hoverId];
    while (frontier.length) {
      const next = [];
      edges.forEach((e) => {
        if (frontier.includes(e.from) && !set.has(e.to)) { set.add(e.to); next.push(e.to); }
      });
      frontier = next;
    }
    frontier = [hoverId];
    while (frontier.length) {
      const next = [];
      edges.forEach((e) => {
        if (frontier.includes(e.to) && !set.has(e.from)) { set.add(e.from); next.push(e.from); }
      });
      frontier = next;
    }
    return set;
  }, [hoverId, edges]);

  // Card geometry for the edge overlay, measured relative to the board. Cards
  // re-measure on mount, model changes, and board resizes (ResizeObserver is
  // absent in jsdom — guarded, edges simply stay at 0,0 in tests).
  const boardRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const nodeRefs = useRef(new Map());
  const [rects, setRects] = useState({});
  const measure = useCallback(() => {
    const board = boardRef.current;
    if (!board) return;
    const b = board.getBoundingClientRect();
    const next = {};
    nodeRefs.current.forEach((el, id) => {
      const r = el.getBoundingClientRect();
      next[id] = { left: r.left - b.left, right: r.right - b.left, midY: r.top - b.top + r.height / 2 };
    });
    setRects(next);
  }, []);
  useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver === "undefined" || !boardRef.current) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(boardRef.current);
    return () => observer.disconnect();
  }, [measure, model]);

  // One ref callback per node id, cached — a fresh callback each render would
  // make React detach and reattach every card's ref on every render, and the
  // board re-renders on every pointermove of a link drag.
  const refCallbacks = useRef(new Map());
  const cardRefFor = useCallback((id) => {
    let cb = refCallbacks.current.get(id);
    if (!cb) {
      cb = (el) => {
        if (el) nodeRefs.current.set(id, el);
        else nodeRefs.current.delete(id);
      };
      refCallbacks.current.set(id, cb);
    }
    return cb;
  }, []);

  const endDrag = useCallback(() => {
    setDrag(null);
    setDragOverId(null);
    setDragCursor(null);
  }, []);

  // While a drag is active, track the cursor for the loose line end and cancel
  // the drag on any pointerup that a card didn't already handle (drop on empty
  // space). Card pointerup (React, at the root) fires before this window
  // handler, so a successful drop still calls onLink first.
  useEffect(() => {
    if (!drag) return undefined;
    const onMove = (e) => {
      const board = boardRef.current;
      if (!board) return;
      const b = board.getBoundingClientRect();
      setDragCursor({ x: e.clientX - b.left, y: e.clientY - b.top });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", endDrag);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", endDrag);
    };
  }, [drag, endDrag]);

  const startDrag = useCallback((id, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDrag(id);
    setDragOverId(null);
    setDragCursor(null);
    setActiveEdge(null);
  }, []);

  const removeLink = () => {
    if (activeEdge) onUnlink?.(activeEdge.from, activeEdge.to);
    setActiveEdge(null);
  };

  const dropOn = useCallback((node) => {
    if (!drag) return;
    const fromNode = model.byId.get(drag);
    if (resolveLinkTarget(fromNode, node)) onLink?.(drag, node.id);
    endDrag();
  }, [drag, model, onLink, endDrag]);

  const linkDragOver = useCallback((id, entering) => {
    if (!drag) return;
    setDragOverId((cur) => (entering ? id : cur === id ? null : cur));
  }, [drag]);

  const nodeClass = (node) => {
    const base = "tocnav-chain-node";
    if (drag) {
      if (node.id === drag) return `${base} is-drag-source`;
      if (resolveLinkTarget(model.byId.get(drag), node)) {
        return `${base} is-drop-ok${dragOverId === node.id ? " is-drop-hover" : ""}`;
      }
      return `${base} is-dim`;
    }
    if (!connected) return base;
    return `${base} ${connected.has(node.id) ? "is-hl" : "is-dim"}`;
  };
  const edgeClass = (e) => {
    if (drag) return "tocnav-edge is-dim";
    if (!connected) return "tocnav-edge";
    return `tocnav-edge ${connected.has(e.from) && connected.has(e.to) ? "is-active" : "is-dim"}`;
  };
  const bezier = (ax, ay, bx, by) => {
    const bend = Math.max(24, (bx - ax) / 2);
    return `M ${ax} ${ay} C ${ax + bend} ${ay}, ${bx - bend} ${by}, ${bx} ${by}`;
  };
  const edgePath = (e) => {
    const a = rects[e.from];
    const b = rects[e.to];
    if (!a || !b) return null;
    return bezier(a.right, a.midY, b.left, b.midY);
  };
  const dragLinePath = () => {
    if (!drag) return null;
    const a = rects[drag];
    if (!a) return null;
    if (dragOverId && rects[dragOverId]) {
      return bezier(a.right, a.midY, rects[dragOverId].left, rects[dragOverId].midY);
    }
    if (dragCursor) return bezier(a.right, a.midY, dragCursor.x, dragCursor.y);
    return null;
  };
  // Midpoint of a symmetric edge bezier (t=0.5) — where the remove control sits.
  const edgeMid = (from, to) => {
    const a = rects[from];
    const b = rects[to];
    if (!a || !b) return null;
    return { x: (a.right + b.left) / 2, y: (a.midY + b.midY) / 2 };
  };
  const activeMid = activeEdge ? edgeMid(activeEdge.from, activeEdge.to) : null;

  // ── drag-to-reorder ──
  const sensors = useSensors(
    useSensor(PointerSensor, POINTER_ACTIVATION),
    useSensor(KeyboardSensor, KEYBOARD_COORDINATES)
  );
  // sortingStep: which column is mid-reorder, so the edge overlay can fade out
  // (the lines are measured from the cards' resting rects and would otherwise
  // trail behind the sliding cards).
  const [sortingStep, setSortingStep] = useState(/** @type {string | null} */ (null));
  // One handler set per column, memoised so DndContext's own memo can bail out
  // on the board's pointermove renders.
  const sortHandlers = useMemo(() => {
    const byStep = {};
    STEP_ORDER.forEach((step) => {
      byStep[step] = {
        onDragStart: () => setSortingStep(step),
        onDragCancel: () => setSortingStep(null),
        onDragEnd: ({ active, over }) => {
          setSortingStep(null);
          if (!over || active.id === over.id) return;
          onReorder?.(step, active.id, over.id);
        },
      };
    });
    return byStep;
  }, [onReorder]);
  // SortableContext compares `items` by reference, so a fresh array each render
  // suppresses the post-drop settle animation.
  const itemIdsByStep = useMemo(() => {
    const byStep = {};
    STEP_ORDER.forEach((step) => {
      byStep[step] = (model.byStep[step] || []).map((n) => n.id);
    });
    return byStep;
  }, [model]);

  // Hover highlight is suppressed during BOTH drags. There is no DragOverlay, so
  // a reorder slides sibling cards under a stationary cursor — without this the
  // whole board flickers between highlighted and dimmed mid-drag.
  const hoverCard = useCallback((id) => {
    if (drag || sortingStep) return;
    setHoverId(id);
    if (id) setActiveEdge(null);
  }, [drag, sortingStep]);

  return (
    <div className="tocnav-chain">
      {/* The item lists scroll inside this container; the SVG edge overlay lives
          inside .tocnav-chain-board (a sibling of the cards) so it scrolls WITH
          them and the lines stay aligned. The add bar below stays pinned. */}
      <div className="tocnav-chain-scroll">
      <div
        className={`tocnav-chain-board${drag ? " is-linking" : ""}${sortingStep ? " is-sorting" : ""}`}
        ref={boardRef}
        onMouseLeave={() => setActiveEdge(null)}
      >
        {STEP_ORDER.map((step, level) => {
          const meta = stepMeta[step];
          const nodes = model.byStep[step] || [];
          // A card gets an outgoing connect-dot only when linking is enabled
          // (onLink present = caller may edit) and it is not the terminal step.
          const canSource = step !== "eff" && !!onLink;
          // One DndContext per column: a card physically cannot be dropped into
          // another column, so a reorder never crosses collections.
          return (
            <DndContext
              key={step}
              sensors={sensors}
              collisionDetection={closestCenter}
              {...sortHandlers[step]}
            >
              <SortableContext items={itemIdsByStep[step]} strategy={verticalListSortingStrategy}>
                <div className="tocnav-chain-col" style={{ "--sc": meta.color }}>
                  {nodes.length === 0 ? (
                    <div className="tocnav-chain-node tocnav-chain-node-empty">
                      Nog geen {meta.label.toLowerCase()}
                    </div>
                  ) : (
                    nodes.map((node) => (
                      <TocChainNode
                        key={node.id}
                        node={node}
                        level={level}
                        className={nodeClass(node)}
                        cardRef={cardRefFor(node.id)}
                        onPick={onPick}
                        onHover={hoverCard}
                        onLinkDragOver={linkDragOver}
                        onLinkDrop={dropOn}
                        onLinkDragStart={startDrag}
                        canSource={canSource}
                        canReorder={!!onReorder && nodes.length > 1}
                        linked={!!syncedLinks?.[node.id]}
                        portfolioName={portfolioNameById?.[syncedLinks?.[node.id]]}
                      />
                    ))
                  )}
                </div>
              </SortableContext>
            </DndContext>
          );
        })}
        <svg className="tocnav-chain-edges" aria-hidden="true">
          {edges.map((e) => {
            const d = edgePath(e);
            if (!d) return null;
            return <path key={`${e.kind}-${e.from}-${e.to}`} d={d} className={edgeClass(e)} />;
          })}
          {onUnlink && !drag && edges.map((e) => {
            const d = edgePath(e);
            if (!d) return null;
            return (
              <path
                key={`hit-${e.kind}-${e.from}-${e.to}`}
                d={d}
                className="tocnav-edge-hit"
                data-edge={`${e.from}-${e.to}`}
                onMouseEnter={() => setActiveEdge({ from: e.from, to: e.to })}
              />
            );
          })}
          {drag && dragLinePath() && <path d={dragLinePath()} className="tocnav-edge tocnav-edge-dragging" />}
        </svg>
        {onUnlink && !drag && activeEdge && activeMid && (
          <button
            type="button"
            className="tocnav-edge-remove"
            style={{ "--ex": `${activeMid.x}px`, "--ey": `${activeMid.y}px` }}
            aria-label="Verbinding verwijderen"
            onMouseLeave={() => setActiveEdge(null)}
            onClick={removeLink}
          >
            ×
          </button>
        )}
      </div>
      </div>

      {/* Fixed add bar under the scrolling lists: one "+ toevoegen" per column,
          aligned to the board's grid. Board adds create UNLINKED items — the
          user drags to connect them. Absent onAdd → read-only board, no bar. */}
      {onAdd && (
        <div className="tocnav-chain-addbar">
          {STEP_ORDER.map((step) => {
            const meta = stepMeta[step];
            return (
              <button
                type="button"
                key={step}
                className="tocnav-chain-add"
                style={{ "--sc": meta.color }}
                onClick={() => onAdd(step)}
              >
                <span className="tocnav-add-plus" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
                <b>{meta.label} toevoegen</b>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TocChainOverview;
